import { Slot } from "@radix-ui/react-slot";
import { Counter } from "../components/Counter";
import { Icon } from "../components/Icon";
import { LinkWrapper } from "../components/LinkWrapper";
import { Spinner } from "../components/Spinner";
import { Tooltip } from "../components/Tooltip";
import { ChevronDown } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

// Redesigned button alongside the existing Button (unchanged). Sizes: 24/32/40px.
// Dark-mode strategy per variant:
//   - primary/outline: explicit dark: gradient overrides (primary goes lighter, outline darker)
//   - highlight/warning: explicit dark: overrides to preserve hue (token ramp would flip to wrong shade)
//   - ghost variants: semantic tokens auto-flip, no dark: needed

export const BUTTON_VARIANTS = ["primary", "highlight", "outline", "warning", "ghost", "ghost-secondary", "highlight-ghost", "warning-ghost"];
export const BUTTON_SIZES = ["xs", "sm", "md"];
// Per-variant (not base) so ghost variants keep ::after free for consumers (e.g. Tabs).
const OVERLAY = cn("after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit]", "after:transition-colors disabled:after:hidden");
const buttonVariants = cva(cn("relative isolate inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap",
// `transform` stays in the transition list for the `press` scale.
"transition-[color,background-color,border-color,transform] duration-100 ease-out", "motion-reduce:transition-none", "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring focus-visible:ring-offset-0", "group-focus-visible/button-link:ring-2 group-focus-visible/button-link:ring-inset group-focus-visible/button-link:ring-ring",
// data-disabled is set only when truly disabled (not loading), so loading buttons
// keep their full-color active look while :disabled guards block interaction for both.
"data-[disabled]:shadow-none", "disabled:cursor-not-allowed disabled:focus-visible:ring-0"), {
  variants: {
    variant: {
      primary: cn(OVERLAY, "bg-primary-800 text-primary-50 shadow-none", "dark:bg-stone-100 dark:text-stone-900", "data-[disabled]:opacity-45"),
      highlight: cn(OVERLAY, "bg-highlight-500 text-white shadow-none", "data-[disabled]:opacity-45"),
      warning: cn(OVERLAY, "bg-warning-600 text-white shadow-none", "data-[disabled]:opacity-45"),
      outline: cn(OVERLAY, "border border-border-dark bg-background text-foreground shadow-none", "hover:after:bg-black/[0.03] dark:hover:after:bg-white/[0.05]", "data-[disabled]:opacity-45"),
      ghost: cn("text-foreground", "hover:bg-black/[0.02] active:bg-black/[0.02]", "dark:hover:bg-white/[0.08] dark:active:bg-white/[0.08]", "data-[disabled]:text-faint", "disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"),
      "ghost-secondary": cn("text-muted-foreground", "hover:bg-black/[0.02] active:bg-black/[0.02]", "dark:hover:bg-white/[0.08] dark:active:bg-white/[0.08]", "data-[disabled]:text-faint", "disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"),
      "highlight-ghost": cn("text-highlight-500", "hover:bg-highlight-50 active:bg-highlight-50", "data-[disabled]:text-highlight-muted", "disabled:hover:bg-transparent"),
      "warning-ghost": cn("text-warning-500", "hover:bg-warning-50 active:bg-warning-50", "data-[disabled]:text-warning-muted", "disabled:hover:bg-transparent")
    },
    size: {
      xs: "h-6 gap-1.5 px-2 text-sm font-medium leading-4 rounded-lg",
      sm: "h-8 gap-1.5 px-3 text-sm font-medium rounded-lg",
      md: "h-10 gap-1.5 px-4 text-base font-medium rounded-lg"
    },
    isIconOnly: {
      true: "",
      false: ""
    },
    press: {
      true: "active:scale-[0.985] motion-reduce:active:scale-100",
      false: ""
    }
  },
  compoundVariants: [{
    size: "xs",
    isIconOnly: true,
    className: "w-6 px-0"
  }, {
    size: "sm",
    isIconOnly: true,
    className: "w-8 px-0"
  }, {
    size: "md",
    isIconOnly: true,
    className: "w-10 px-0"
  },
  // Solid raised variants: larger buttons get a stronger white overlay (Figma spec).
  // dark:hover on primary is overridden per-variant to darken instead of lighten.
  {
    variant: ["primary", "highlight", "warning"],
    size: ["xs", "sm"],
    className: "hover:after:bg-white/10 active:after:bg-white/10"
  }, {
    variant: ["primary", "highlight", "warning"],
    size: "md",
    className: "hover:after:bg-white/20 active:after:bg-white/20"
  },
  // Outline dark: white overlay (dark surface needs lighten, not darken).
  {
    variant: "outline",
    size: ["xs", "sm", "md"],
    className: "dark:hover:after:bg-white/[0.02] dark:active:after:bg-white/[0.02]"
  }],
  defaultVariants: {
    variant: "primary",
    size: "sm",
    isIconOnly: false,
    press: true
  }
});

// Labels and icons carry a subtle shadow on raised (non-ghost) variants.
const RAISED_VARIANTS = ["primary", "highlight", "warning", "outline"];
const TEXT_SHADOW = "[text-shadow:0_1px_1.5px_rgba(0,0,0,0.08)]";
const ICON_SHADOW = "drop-shadow-[0px_1px_0.75px_rgba(0,0,0,0.08)]";
const INTERNAL_ICON_SIZE_MAP = {
  xs: "xs",
  sm: "xs",
  md: "sm"
};
const COUNTER_SIZE_MAP = {
  xs: "xs",
  sm: "xs",
  md: "sm"
};
const chevronVariantMap = {
  // primary swaps to a light button in dark mode, so its chevron tracks the
  // flipping text token rather than a fixed white.
  primary: "text-primary-50/60",
  highlight: "text-white/60",
  warning: "text-white/60",
  outline: "text-faint",
  ghost: "text-faint",
  "ghost-secondary": "text-faint",
  "highlight-ghost": "text-highlight-400",
  "warning-ghost": "text-warning-400"
};

// Loading spinner color, matched to each variant's text color (including the
// dark-mode swap: `revert` = light spinner on light theme, `mono` = the
// inverse). Keeps the spinner the same color as the label.
const spinnerVariantMap = {
  primary: "revert",
  highlight: "light",
  warning: "light",
  outline: "mono",
  ghost: "mono",
  "ghost-secondary": "mono",
  "highlight-ghost": "blue500",
  "warning-ghost": "red500"
};
function isReactElement(visual) {
  return React.isValidElement(visual);
}

// Backward-compat aliases and mappings for internal sparkle components that
// previously imported from the old Button.tsx. No other files need to change.

const LEGACY_SIZE_MAP = {
  icon: "sm",
  "icon-xs": "xs",
  "icon-sm": "md",
  mini: "sm",
  xmini: "xs"
};
const LEGACY_VARIANT_MAP = {
  "highlight-secondary": "highlight-ghost",
  "warning-secondary": "warning-ghost"
};
export const ICON_SIZE_MAP = {
  xs: "xs",
  sm: "sm",
  md: "md"
};
/**
 * Lets users trigger an action or event — submitting a form, opening a dialog, or
 * confirming a choice — in several visual variants and three sizes (xs / sm / md),
 * with leading/trailing icons, loading and disabled states, an inline counter, and
 * a dropdown-chevron affordance (`isSelect`). Use a single `highlight` button per
 * view with `outline` or ghost variants for secondary actions; give icon-only
 * buttons a `tooltip` and set `isLoading` during async work.
 * @summary Action button.
 */
const Button = React.forwardRef(({
  label,
  icon,
  iconRight,
  className,
  variant = "primary",
  size = "sm",
  isSelect = false,
  isLoading = false,
  isCounter = false,
  counterValue,
  isPulsing = false,
  isRounded = false,
  tooltip,
  tooltipShortcut,
  href,
  target,
  rel,
  replace,
  shallow,
  "aria-label": ariaLabel,
  hasLighterFont: _hasLighterFont,
  ...props
}, ref) => {
  var _ref, _ref2;
  const normalizedSize = size != null && size in LEGACY_SIZE_MAP ? LEGACY_SIZE_MAP[size] : (_ref = size) !== null && _ref !== void 0 ? _ref : "sm";
  const normalizedVariant = variant != null && variant in LEGACY_VARIANT_MAP ? LEGACY_VARIANT_MAP[variant] : (_ref2 = variant) !== null && _ref2 !== void 0 ? _ref2 : "primary";
  const iconSize = INTERNAL_ICON_SIZE_MAP[normalizedSize];
  const showCounter = isCounter && counterValue != null;
  const isIconOnly = !label && !showCounter && !isSelect && !!icon;
  // Menu triggers (dropdown/popover/select) skip the press scale, else the
  // opening menu jumps with the anchor. Radix sets aria-haspopup on the
  // trigger; isSelect is our chevron affordance. Tooltips don't, so they keep it.
  const isMenuTrigger = isSelect || props["aria-haspopup"] != null;
  const hasTextShadow = normalizedVariant != null && RAISED_VARIANTS.includes(normalizedVariant);
  const iconShadow = hasTextShadow ? ICON_SHADOW : "";
  const renderIcon = (visual, extraClass = "") => {
    if (isReactElement(visual)) {
      return /*#__PURE__*/React.createElement("span", {
        className: cn("shrink-0", extraClass)
      }, visual);
    }
    return /*#__PURE__*/React.createElement(Icon, {
      visual: visual,
      size: iconSize,
      className: extraClass
    });
  };
  const restingContent = /*#__PURE__*/React.createElement(React.Fragment, null, icon && renderIcon(icon, iconShadow), label && /*#__PURE__*/React.createElement("span", {
    className: cn(hasTextShadow && TEXT_SHADOW)
  }, label), showCounter && /*#__PURE__*/React.createElement(Counter, {
    value: Number(counterValue),
    variant: "primary",
    size: COUNTER_SIZE_MAP[normalizedSize],
    isInButton: true
  }), iconRight && renderIcon(iconRight, iconShadow), isSelect && /*#__PURE__*/React.createElement(Icon, {
    visual: ChevronDown,
    size: iconSize,
    className: normalizedVariant ? chevronVariantMap[normalizedVariant] : ""
  }));

  // Loading keeps the button at its resting size: the normal content stays
  // in the layout but invisible, and a centered spinner overlays it.
  const content = isLoading ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "absolute inset-0 flex items-center justify-center"
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: "xs",
    variant: spinnerVariantMap[normalizedVariant]
  })), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: "invisible flex items-center gap-[inherit]"
  }, restingContent)) : restingContent;
  const pointerEventProps = React.useMemo(() => {
    if (isLoading || props.disabled) {
      return {
        onPointerDown: e => {
          e.preventDefault();
          e.stopPropagation();
        }
      };
    }
    return {};
  }, [isLoading, props.disabled]);

  // Disabled/loading buttons must not navigate: render a real <button> and
  // skip the LinkWrapper below.
  const isInteractive = !props.disabled && !isLoading;
  const shouldUseSlot = !!href && isInteractive;
  const Comp = shouldUseSlot ? Slot : "button";
  const innerButton = /*#__PURE__*/React.createElement(Comp, {
    ref: ref,
    className: cn(buttonVariants({
      variant: normalizedVariant,
      size: normalizedSize,
      isIconOnly,
      press: !isMenuTrigger && !isLoading
    }), isPulsing && "animate-ring-pulse-soft", isRounded && "rounded-full", isLoading && "relative", className),
    "aria-label": ariaLabel || tooltip || label,
    ...props,
    // Loading blocks interaction (disabled attr) but keeps the active look:
    // the muted `data-[disabled]:` styles are gated on this attribute, which
    // is set only when truly disabled — so a loading button stays full-color
    // and its spinner matches the label. Set after {...props} so a
    // consumer-passed `disabled` can't clobber the computed value.
    disabled: isLoading || props.disabled,
    "data-disabled": props.disabled && !isLoading ? "" : undefined,
    "aria-busy": isLoading || undefined,
    ...pointerEventProps
  }, shouldUseSlot ? /*#__PURE__*/React.createElement("span", null, content) : content);
  const wrappedContent = tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    trigger: innerButton,
    tooltipTriggerAsChild: true,
    label: tooltip,
    shortcut: tooltipShortcut
  }) : innerButton;
  return href && isInteractive ? /*#__PURE__*/React.createElement(LinkWrapper, {
    className: "group/button-link focus-visible:outline-hidden",
    href: href,
    target: target,
    rel: rel,
    replace: replace,
    shallow: shallow
  }, wrappedContent) : wrappedContent;
});
Button.displayName = "Button";
export { Button, buttonVariants };
//# sourceMappingURL=Button.js.map